import Tool, {
  BaseTool,
  type IToolEnabledInput,
  type ToolButtonComponent,
} from './Tool';
import React, { useRef } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolbarRow from '../toolbars/ToolbarRow';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import {
  type EmbeddedTextBehaviors,
  type EmbeddedTextFormatQueryable,
} from '../objects/EmbeddedTextBehaviorsV2';

import Shortcut from '../utils/Shortcut';
import { hasEmbeddedTextBehaviors } from '../objects/guards';
import {
  tools as AhaEditorTools,
  AhaEditorUtils,
} from '@aha-app/aha-editor-v2';

const ListButton = (tool: typeof ToolTextFormatOption, icon: string) => {
  const Button: React.FC<{
    active: boolean;
    setFormat: (tool: typeof ToolTextFormatOption) => void;
    hidePopup: () => void;
  }> = ApplicationView(({ active, setFormat, hidePopup }) => (
    <ToolButton
      active={active}
      onClick={() => {
        setFormat(tool);
        hidePopup();
      }}
      tool={tool}
    >
      <aha-icon icon={icon} />
    </ToolButton>
  ));
  return Button;
};

class ToolTextFormatOption extends BaseTool {
  static formatType: string;

  static action({
    controller,
    selection,
  }: {
    controller: WhiteboardController;
    selection: fabric.Object & EmbeddedTextBehaviors;
  }) {
    const hasFormat = selection.selectionIsEntirely(this.formatType);
    if (hasFormat) {
      selection.applyTextProperty(editor => {
        AhaEditorUtils.Marks.remove(editor, this.formatType);
      });
    } else {
      selection.applyTextProperty(editor => {
        AhaEditorUtils.Marks.add(editor, this.formatType, true);
      });
    }

    selection.setOnObjects(this.formatType, !hasFormat);
    controller.canvas.requestRenderAll();
  }

  static enabled({ selection }: IToolEnabledInput): boolean {
    return selection?.getObjects().some(hasEmbeddedTextBehaviors) ?? false;
  }
}

class ToolTextFormatBold extends ToolTextFormatOption {
  static toolName = 'Bold';
  static formatType = 'bold';
  static shortcut = new Shortcut('Cmd+B');

  static buttonComponent() {
    return ListButton(ToolTextFormatBold, 'fa-regular fa-bold');
  }
}

class ToolTextFormatItalic extends ToolTextFormatOption {
  static toolName = 'Italic';
  static formatType = 'italic';
  static shortcut = new Shortcut('Cmd+I');

  static buttonComponent() {
    return ListButton(ToolTextFormatItalic, 'fa-regular fa-italic');
  }
}

class ToolTextFormatUnderline extends ToolTextFormatOption {
  static toolName = 'Underline';
  static formatType = 'underline';
  static shortcut = new Shortcut('Cmd+U');

  static buttonComponent() {
    return ListButton(ToolTextFormatUnderline, 'fa-regular fa-underline');
  }
}

class ToolTextFormatStrikeThrough extends ToolTextFormatOption {
  static toolName = 'Strikethrough';
  static formatType = 'strikethrough';
  static shortcut = new Shortcut('Shift+Cmd+X');

  static buttonComponent() {
    return ListButton(
      ToolTextFormatStrikeThrough,
      'fa-regular fa-strikethrough'
    );
  }
}

class ToolTextFormatCode extends ToolTextFormatOption {
  static toolName = 'Code snippet';
  static formatType = 'code';
  static shortcut = new Shortcut('Cmd+/');

  static action({
    controller,
    selection,
  }: {
    controller: WhiteboardController;
    selection: fabric.Object & EmbeddedTextBehaviors;
  }) {
    const hasFormat = selection.selectionIsEntirely(this.formatType);
    selection.applyTextProperty(editor => {
      AhaEditorTools.ToolCodeSnippet.action({ editor });
    });
    selection.setOnObjects(this.formatType, !hasFormat);
    controller.canvas.requestRenderAll();
  }

  static buttonComponent() {
    return ListButton(ToolTextFormatCode, 'fa-regular fa-code');
  }
}

const Popup: React.FC<{
  selection: fabric.Object & EmbeddedTextFormatQueryable;
  hidePopup: () => void;
}> = ApplicationView(({ selection, hidePopup }) => {
  const controller = useController(WhiteboardController);

  const tools = [
    ToolTextFormatBold,
    ToolTextFormatItalic,
    ToolTextFormatUnderline,
    ToolTextFormatStrikeThrough,
    ToolTextFormatCode,
  ];

  const buttons = tools.map(tool => {
    return { tool, Component: tool.buttonComponent() };
  });

  const setFormat = tool => {
    controller.actionApplyTool(tool, {}, false);
  };

  return (
    <ToolbarMenu columns={5}>
      <ToolbarRow>
        {buttons.map(({ tool, Component }, index) => (
          <Component
            key={index}
            setFormat={setFormat}
            hidePopup={hidePopup}
            active={selection.selectionIsEntirely(tool.formatType)}
          />
        ))}
      </ToolbarRow>
    </ToolbarMenu>
  );
});

const TextFormatButton: ToolButtonComponent<
  fabric.Object & EmbeddedTextFormatQueryable
> = ApplicationView(({ selection, tool }) => {
  const tippyRef = useRef<TippyInstance>();

  return (
    <ToolButton
      popup={
        <Popup
          selection={selection}
          hidePopup={() => tippyRef.current?.hide()}
        />
      }
      tippyRef={tippyRef}
      tool={tool}
    >
      <aha-icon icon='fa-regular fa-bold' />
    </ToolButton>
  );
});

class ToolTextFormat extends Tool {
  static toolName = 'Format';

  static buttonComponent() {
    return TextFormatButton;
  }
}

export {
  ToolTextFormat,
  ToolTextFormatBold,
  ToolTextFormatItalic,
  ToolTextFormatUnderline,
  ToolTextFormatStrikeThrough,
  ToolTextFormatCode,
};
